import { Component, Input, Output, EventEmitter, inject, DestroyRef, ViewChild, OnInit } from '@angular/core';

import { MatIconModule } from '@angular/material/icon';
import { GridComponent, GridModule } from '@mds/ui';
import { MenuService } from '@services/menu.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReusableService } from '@services/reusable.service';
import { GlobalDataService } from '@services/global-data.service';
import { CellKeyDownEvent, FullWidthCellKeyDownEvent } from 'ag-grid-community';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import { Router } from '@angular/router';

@Component({
  selector: 'app-report-content',
  standalone: true,
  imports: [MatIconModule, GridModule, MdsIconsModule],
  templateUrl: './report-content.component.html',
  styleUrls: ['./report-content.component.scss']
})
export class ReportContentComponent implements OnInit {
  @Input() reportGridData: any;
  @Input() selectedRow: any;
  @Input() loading: boolean;
  @Input() loadingMessage: string;
  destroyRef = inject(DestroyRef);
  menuService = inject(MenuService);
  reusableService = inject(ReusableService);
  globalDataService = inject(GlobalDataService);
  router = inject(Router);
  showDefaultCharts = false;
  reportData: any;
  templateData: any;
  templateInfo: any;
  gridSettings: string;
  icons = MdsIcon;

  @ViewChild(GridComponent) gridComponent: GridComponent;
  @Output() onGridKeyPressed: EventEmitter<CellKeyDownEvent | FullWidthCellKeyDownEvent> = new EventEmitter<
    CellKeyDownEvent | FullWidthCellKeyDownEvent
  >();

  #gridOptions: any;
  @Input() set gridOptions(options: any) {
    this.#gridOptions = options;
    if (options) {
      this.menuService.updateMenuItem('show_chart', { visible: true });
      this.#gridOptions.getContextMenuItems = this.getContextMenuItems.bind(this);
    }
  }

  get gridOptions() {
    return this.#gridOptions;
  }

  @Input() templateBody: any;

  @Output() onReloadReport: EventEmitter<void> = new EventEmitter<void>();

  reloadReport() {
    this.onReloadReport.emit();
  }

  ngOnInit() {
    this.menuService.selectedAction$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (value?.key === 'show_chart') {
        if (this.showDefaultCharts === value) {
          this.showDefaultCharts = !value;
          setTimeout(() => {
            this.showDefaultCharts = value;
          }, 0);
        } else {
          this.showDefaultCharts = value;
        }
      } else if (value?.key === 'history') {
        this.router.navigate(['/reports/history']);
      }
      this.updateGridSettings();
    });
    this.reusableService.reportData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.reportData = value;
    });
    this.reusableService.templateOrReportData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.templateData = value;
      if (this.templateData?.grid_settings) {
        this.gridSettings = this.templateData?.grid_settings;
      }
    });
  }

  updateGridSettings() {
    const gridSetting = {
      columnState: this.gridComponent?.api?.getColumnState(),
      filterSettings: this.gridComponent?.api?.getFilterModel(),
      pivotMode: this.gridComponent?.api?.isPivotMode(),
      chartSettings: this.gridComponent?.api?.getChartModels()
    };
    this.globalDataService.setGridSettings(gridSetting);
  }

  onCellKeyPressed(event: CellKeyDownEvent | FullWidthCellKeyDownEvent) {
    this.onGridKeyPressed.emit(event);
  }

  public getContextMenuItems(params) {
    const menuItems = this.getFilteredDefaultMenuItems(params);
    if (!menuItems || !params.node?.data) return;

    const { data } = params.node;
    const colField = params.column.colDef.field;
    const { headerName } = params.column.colDef;
    const link = this.getContextLink(data, colField, headerName);

    if (link) {
      menuItems.unshift('separator');
      menuItems.unshift({
        name: link.name,
        action: () => setTimeout(() => link.action())
      });
    }

    menuItems.push(this.getExportMenu(data));
    return menuItems;
  }

  // -------------------- Helper Functions --------------------

  private getFilteredDefaultMenuItems(params): any[] {
    return params.defaultItems?.filter((item) => !['export', 'chartRange'].includes(item));
  }

  private getContextLink(data: any, colField: string, headerName: string): { name: string; action: () => void } | null {
    const unsupportedTypes = ['Folder', 'folder', 'Bom Object', 'File', 'file'];
    if (unsupportedTypes.includes(data.type)) return null;

    const link: { name?: string; action?: () => void } = {};

    if (data.rid) data.rid = data.rid.replace('#', '');

    if (data.link?.url) {
      link.name = 'Open link in new tab';
      link.action = () => window.open(`${this.getLinkUrl(data)}`, '_blank');
    } else if (data.rid) {
      const route = this.getRouteFromType(data.type, data.rid);
      if (route) {
        link.name = 'Open link in new tab';
        link.action = () => window.open(`${route}`, '_blank');
      }
    }

    if (data.row?.length) {
      const rowLink = this.getLinkFromRow(data.row, colField, headerName);
      if (rowLink) return rowLink;
    }

    return link.name && link.action ? (link as { name: string; action: () => void }) : null;
  }

  private getRouteFromType(type: string, rid: string): string | null {
    const routeMap = {
      'Multi-Criteria Comparison': `/mcda/${rid}`,
      Quest: `/quests/${rid}`
    };
    return routeMap[type] || null;
  }

  getLinkUrl(data: any) {
    let link = `/${data?.link?.url}`;
    if (data?.link?.url.startsWith('composer')) {
      const urlParts = data.link.url.split('/');
      let rid = data.rid || urlParts[urlParts.length - 1]; // get "rid" from the url if rid doesn't exist in data.
      rid = rid.replace('#', '');
      link = `/mattermaps/overview/${rid}/edit`;
    }
    return link;
  }

  private getLinkFromRow(
    row: any[],
    colField: string,
    headerName: string
  ): { name: string; action: () => void } | null {
    let gotOnce = false;

    for (const e of row) {
      if (e.key.endsWith(colField) && e.link) {
        if (gotOnce && headerName !== e.label) continue;
        gotOnce = true;

        const { url } = e.link;
        if (url) {
          return {
            name: 'Open link in new tab',
            action: () => window.open(this.getLinkUrl(e), '_blank')
          };
        }
      }
    }

    return null;
  }

  private getExportMenu(data: any): any {
    const fileName = data.name || data.title || 'folder';
    return {
      name: 'Export To',
      subMenu: [
        {
          name: 'CSV Export',
          action: () => this.gridExportHandler('csv')
        },
        {
          name: 'Excel Export (.xlsx)',
          action: () => {
            this.gridExportHandler('xlsx');
          }
        }
      ]
    };
  }

  private getOriginalRowCell(params: any): any {
    const field = params.column?.getColDef?.()?.field;
    const row = params.node?.data?.row;

    if (!field || !Array.isArray(row)) {
      return null;
    }

    return row.find((cell) => {
      let keyList = [];

      if (cell.key.includes('.')) {
        keyList = cell.key.split('.');
      } else if (cell.key.includes('$')) {
        keyList = cell.key.split('$');
      } else {
        keyList.push(cell.key);
      }

      return keyList[keyList.length - 1] === field;
    });
  }

  private processExportCell(params: any): any {
    const originalCell = this.getOriginalRowCell(params);

    if (originalCell && typeof originalCell.value !== 'number') {
      return originalCell.display_value;
    }

    return params.value;
  }

  public gridExportHandler(mode: 'csv' | 'xlsx' | 'xml'): void {
    const columnsToExport = this.gridComponent?.api
      .getAllDisplayedColumns()
      .filter((col) => {
        const def = col.getColDef?.();
        return def?.headerName !== 'Actions' && !!def?.field && !def?.field.startsWith('mapped_');
      })
      .map((col) => col.getColId());

    // Default export params: only visible columns
    const params = {
      allColumns: false,
      columnKeys: columnsToExport,
      processCellCallback: this.processExportCell.bind(this)
    };

    if (mode === 'csv') {
      // Use AG Grid built-in export functionality
      this.gridComponent?.api.exportDataAsCsv(params);
    } else if (mode === 'xlsx') {
      this.gridComponent?.api.exportDataAsExcel(params);
    }
  }
}
